import React, { useEffect, useRef, useState } from 'react';
import { FaCheck, FaEye, FaMoneyBillWave, FaSync, FaTimes, FaUsers } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { Modal } from 'antd';
import StatusToast from '../../components/StatusToast/StatusToast';
import {
  deleteClubApplication,
  getAllClubApplications,
  updateClubApplicationStatus
} from '../../services/clubApplicationService';
import { isAdminUser } from '../../services/authService';
import './ClubApplications.scss';

const ClubApplications = () => {
  const navigate = useNavigate();
  const isAdmin = isAdminUser();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [statusNotes, setStatusNotes] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [toast, setToast] = useState(null);
  const toastTimerRef = useRef(null);

  useEffect(() => {
    fetchApplications();

    return () => {
      if (toastTimerRef.current) {
        clearTimeout(toastTimerRef.current);
      }
    };
  }, []);

  const showToast = (type, message, duration = 4000) => {
    if (toastTimerRef.current) {
      clearTimeout(toastTimerRef.current);
    }

    setToast({ type, message });
    toastTimerRef.current = setTimeout(() => setToast(null), duration);
  };

  const fetchApplications = async () => {
    setLoading(true);
    try {
      const response = await getAllClubApplications();
      if (response?.isSuccess === false) {
        showToast('error', response?.resultMessage || 'Başvurular yüklenemedi');
      } else {
        const applicationList = Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response)
            ? response
            : [];
        setApplications(applicationList);
      }
    } catch (error) {
      console.error('Başvurular yüklenirken hata:', error);
      showToast('error', 'Başvurular yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async () => {
    if (!isAdmin) {
      showToast('error', 'Bu işlem için yöneticiniz ile görüşün.');
      return;
    }

    if (!selectedApplication || !newStatus) {
      return;
    }

    try {
      const response = await updateClubApplicationStatus(
        selectedApplication.id,
        newStatus,
        statusNotes
      );

      if (response?.isSuccess !== false) {
        showToast('success', 'Başvuru durumu güncellendi');
        setShowStatusModal(false);
        setStatusNotes('');
        fetchApplications();
      } else {
        showToast('error', response?.resultMessage || 'Durum güncellenemedi');
      }
    } catch (error) {
      console.error('Durum güncellenirken hata:', error);
      showToast('error', 'Durum güncellenirken bir hata oluştu');
    }
  };

  const handleReject = async (application) => {
    if (!isAdmin) {
      showToast('error', 'Bu işlem için yöneticiniz ile görüşün.');
      return;
    }

    if (application.assignmentCount > 0) {
      showToast(
        'error',
        `Bu başvuruya ${application.assignmentCount} adet saat ataması yapılmış. Önce atamaları kaldırın.`
      );
      return;
    }

    Modal.confirm({
      title: 'Onay',
      content: `${application.firstName} ${application.lastName} başvurusu reddedilecek ve listeden silinecektir. Onaylıyor musunuz?`,
      okText: 'Evet',
      cancelText: 'Hayır',
      onOk: async () => {
        try {
          const statusResponse = await updateClubApplicationStatus(
            application.id,
            'Rejected',
            'Saat ataması olmadığı için reddedildi'
          );

          if (statusResponse?.isSuccess === false) {
            showToast('error', statusResponse?.resultMessage || 'Başvuru reddedilemedi');
            return;
          }

          const deleteResponse = await deleteClubApplication(application.id);
          if (deleteResponse?.isSuccess !== false) {
            showToast('success', 'Başvuru reddedildi ve listeden kaldırıldı');
            fetchApplications();
          } else {
            showToast('error', deleteResponse?.resultMessage || 'Başvuru silinemedi');
          }
        } catch (error) {
          console.error('Reddetme hatası:', error);
          showToast('error', 'Başvuru reddedilirken bir hata oluştu');
        }
      }
    });
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'Approved':
        return 'Onaylandı';
      case 'Rejected':
        return 'Reddedildi';
      case 'Pending':
        return 'Beklemede';
      default:
        return status;
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'Approved':
        return 'status-approved';
      case 'Rejected':
        return 'status-rejected';
      case 'Pending':
        return 'status-pending';
      default:
        return '';
    }
  };

  const formatDate = (dateString) =>
    new Date(dateString).toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });

  const filteredApplications = statusFilter
    ? applications.filter((app) => app.status === statusFilter)
    : applications;

  return (
    <div className="club-applications-page">
      <StatusToast toast={toast} onClose={() => setToast(null)} />

      <div className="page-header">
        <div className="header-content">
          <FaUsers className="header-icon" />
          <div>
            <h1>Kulüp Bilgisi</h1>
            <p>Kulüp hocası başvurularını görüntüleyin ve yönetin</p>
          </div>
        </div>
        <div className="header-actions">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="status-filter"
          >
            <option value="">Tümü</option>
            <option value="Pending">Beklemede</option>
            <option value="Approved">Onaylandı</option>
            <option value="Rejected">Reddedildi</option>
          </select>
          <button className="refresh-btn" onClick={fetchApplications}>
            <FaSync /> Yenile
          </button>
        </div>
      </div>

      <div className="table-container">
        {loading ? (
          <div className="loading-state">Yükleniyor...</div>
        ) : filteredApplications.length === 0 ? (
          <div className="empty-state">Başvuru bulunamadı</div>
        ) : (
          <table className="applications-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Ad Soyad</th>
                <th>Tesis</th>
                <th>Sorumlu Günler</th>
                <th>Telefon</th>
                <th>Durum</th>
                <th>Başvuru Tarihi</th>
                <th>İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {filteredApplications.map((app) => (
                <tr key={app.id}>
                  <td>{app.id}</td>
                  <td>
                    {app.firstName} {app.lastName}
                  </td>
                  <td>{app.facilityName || '-'}</td>
                  <td>
                    <div className="days-tags">
                      {app.responsibleDays
                        ?.split(',')
                        .slice(0, 3)
                        .map((day, idx) => (
                          <span key={idx} className="day-tag">
                            {day.trim()}
                          </span>
                        ))}
                      {app.responsibleDays?.split(',').length > 3 && (
                        <span className="day-tag more">
                          +{app.responsibleDays.split(',').length - 3}
                        </span>
                      )}
                    </div>
                  </td>
                  <td>{app.phone}</td>
                  <td>
                    <span className={`status-badge ${getStatusClass(app.status)}`}>
                      {getStatusLabel(app.status)}
                    </span>
                  </td>
                  <td>{formatDate(app.createdAt)}</td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="action-btn detail"
                        onClick={() => {
                          setSelectedApplication(app);
                          setShowDetailModal(true);
                        }}
                        title="Detay"
                      >
                        <FaEye />
                      </button>
                      {isAdmin && (
                        <>
                          <button
                            className="action-btn approve"
                            onClick={() => {
                              setSelectedApplication(app);
                              setNewStatus('Approved');
                              setShowStatusModal(true);
                            }}
                            title="Onayla"
                            disabled={app.status === 'Approved'}
                          >
                            <FaCheck />
                          </button>
                          <button
                            className="action-btn delete"
                            onClick={() => handleReject(app)}
                            title="Reddet"
                            disabled={app.status === 'Rejected'}
                          >
                            <FaTimes />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showDetailModal && selectedApplication && (
        <div className="modal-overlay" onClick={() => setShowDetailModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Başvuru Detayı</h3>
              <button className="modal-close" onClick={() => setShowDetailModal(false)}>
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="detail-section">
                <h4>Kişisel Bilgiler</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Ad Soyad</label>
                    <span>
                      {selectedApplication.firstName} {selectedApplication.lastName}
                    </span>
                  </div>
                  <div className="detail-item">
                    <label>T.C. Kimlik No</label>
                    <span>{selectedApplication.identityNumber}</span>
                  </div>
                  <div className="detail-item">
                    <label>Vergi Numarası</label>
                    <span>{selectedApplication.taxNumber}</span>
                  </div>
                  <div className="detail-item">
                    <label>E-Posta</label>
                    <span>{selectedApplication.email}</span>
                  </div>
                  <div className="detail-item">
                    <label>Telefon</label>
                    <span>{selectedApplication.phone}</span>
                  </div>
                </div>
              </div>

              <div className="detail-section">
                <h4>Tesis Bilgileri</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Tesis</label>
                    <span>{selectedApplication.facilityName || 'Belirtilmemiş'}</span>
                  </div>
                  <div className="detail-item full-width">
                    <label>Sorumlu Günler</label>
                    <div className="days-tags-detail">
                      {selectedApplication.responsibleDays?.split(',').map((day, idx) => (
                        <span key={idx} className="day-tag">
                          {day.trim()}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div className="detail-section">
                <h4>Durum</h4>
                <div className="status-display">
                  <span className={`status-badge large ${getStatusClass(selectedApplication.status)}`}>
                    {getStatusLabel(selectedApplication.status)}
                  </span>
                  <span className="created-date">
                    Başvuru: {formatDate(selectedApplication.createdAt)}
                  </span>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              {isAdmin && (
                <>
                  <button
                    className="btn btn-success"
                    onClick={() => {
                      setNewStatus('Approved');
                      setShowDetailModal(false);
                      setShowStatusModal(true);
                    }}
                    disabled={selectedApplication.status === 'Approved'}
                  >
                    Onayla
                  </button>
                  <button
                    className="btn btn-danger"
                    onClick={() => {
                      setShowDetailModal(false);
                      handleReject(selectedApplication);
                    }}
                    disabled={selectedApplication.status === 'Rejected'}
                  >
                    Reddet
                  </button>
                </>
              )}
              <button className="btn btn-secondary" onClick={() => setShowDetailModal(false)}>
                Kapat
              </button>
              {selectedApplication.status === 'Approved' && (
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    setShowDetailModal(false);
                    navigate('/club-payments', { state: { taxNumber: selectedApplication.taxNumber } });
                  }}
                  style={{ marginLeft: 'auto' }}
                >
                  <FaMoneyBillWave style={{ marginRight: '8px' }} />
                  Ödeme ve Takip
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {showStatusModal && (
        <div className="modal-overlay" onClick={() => setShowStatusModal(false)}>
          <div className="modal-content small" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Durum Değiştir</h3>
              <button className="modal-close" onClick={() => setShowStatusModal(false)}>
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Yeni Durum</label>
                <select
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value)}
                  className="form-select"
                >
                  <option value="">Durum seçin</option>
                  <option value="Pending">Beklemede</option>
                  <option value="Approved">Onaylandı</option>
                  <option value="Rejected">Reddedildi</option>
                </select>
              </div>
              <div className="form-group">
                <label>Not (Opsiyonel)</label>
                <textarea
                  value={statusNotes}
                  onChange={(e) => setStatusNotes(e.target.value)}
                  rows={3}
                  placeholder="İşlem notu ekleyin..."
                  className="form-textarea"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowStatusModal(false)}>
                İptal
              </button>
              <button className="btn btn-success" onClick={handleStatusChange}>
                Kaydet
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClubApplications;
